import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import { object, number, boolean, string } from 'yup';
import Modal from '../Modal/Modal';
import InputControl from '../InputControl/InputControl';
import InteractiveButton from '../InteractiveButton/InteractiveButton';
import { Dispatch } from '../../store/store';
import { connect } from 'react-redux';
import SpinnerLoader from '../SpinnerLoader/SpinnerLoader';
import { ChartPreference, UpdateChartPreferenceDto } from '../../store/models/chart-preference';
import InputDateControl from '../InputDateTimeControl/InputDateTimeControl';
import CycleMeasurement from '../../enums/cycle-measurement';
import CycleMeasurementSymbol from '../../enums/cycle-measurement-symbol';
import getTempByMeasurement from '../../utils/getTempByMeasurement';
import fahrenheitToCelsius from '../../utils/fahrenheitToCelsius';
import { useTranslation } from 'react-i18next';
import SelectOption, { Option } from '../SelectOption/SelectOption';
import { DATA_RETRIEVAL_INTERVAL } from '../../constants/common';

type ChartPreferencesFormValues = UpdateChartPreferenceDto & { is_now: boolean };
type DispatchProps = ReturnType<typeof mapDispatch>;

export type PreferencesTempRange = Pick<ChartPreference, 'min_temperature' | 'max_temperature'> | null

type ChartPreferencesProps = {
  cycleId: string;
  isVisible: boolean;
  onOpen: () => void;
  onClose: (isChangedValues: boolean) => void;
  chartPreference: ChartPreference,
  measurement: CycleMeasurement,
  currentTempRange: PreferencesTempRange,
}
type ComponentProps = ChartPreferencesProps & DispatchProps

const ChartPreferences: React.FC<ComponentProps> = ({
  onClose,
  isVisible,
  getOneByCycleId,
  save,
  cycleId,
  chartPreference,
  measurement,
  currentTempRange,
}) => {
  const { t } = useTranslation();
  const [isFirstLoad, setIsFirstLoad] = useState<boolean>(true);

  const timeGridOptions: Option[] = [
    { id: String(10 * 60 * 1000), value: '10 minutes' },
    { id: String(15 * 60 * 1000), value: '15 minutes' },
    { id: String(30 * 60 * 1000), value: '30 minutes' },
    { id: String(1 * 60 * 60 * 1000), value: '1 hour' },
    { id: String(2 * 60 * 60 * 1000), value: '2 hours' },
    { id: String(4 * 60 * 60 * 1000), value: '4 hours' },
    { id: String(6 * 60 * 60 * 1000), value: '6 hours' },
    { id: String(12 * 60 * 60 * 1000), value: '12 hours' },
    { id: String(24 * 60 * 60 * 1000), value: '24 hours' },
  ]

  const chartPreferencesFormik = useFormik<ChartPreferencesFormValues>({
    initialValues: {
      id: chartPreference.id,
      temperature_grid_size: chartPreference.temperature_grid_size,
      min_temperature: chartPreference.min_temperature,
      max_temperature: chartPreference.max_temperature,
      start_timestamp: chartPreference.start_timestamp,
      end_timestamp: chartPreference.end_timestamp,
      time_grid_side: chartPreference.time_grid_side,
      is_now: chartPreference.end_timestamp === null,
    },
    validationSchema: object({
      temperature_grid_size: number()
        .required('Required')
        .integer('Must be an integer')
        .positive('Must be positive')
        .typeError('Must be a number'),
      min_temperature: number()
        .required('Required')
        .integer('Must be an integer')
        .typeError('Must be a number')
        .test('min-less-than-max', 'The minimum value must be less than the maximum', function (value) {
          return value < this.parent.max_temperature;
        }),
      max_temperature: number()
        .required('Required')
        .integer('Must be an integer')
        .typeError('Must be a number')
        .test('max-greater-than-min', 'The maximum value must be greater than the minimum', function (value) {
          return value > this.parent.min_temperature;
        }),
      start_timestamp:
        number()
          .required('Required')
          .integer('Must be an integer')
          .typeError('Must be a number')
          .test(
            'date_must_be_now_or_earlier',
            'Start time must be the current time or earlier',
            function (value) {
              if (typeof value !== 'number') return true;
              const now = Date.now();
              return value < now;
            }
          ),
      end_timestamp:
        number()
          .nullable()
          .typeError('Must be a number')
          .test(
            'is-at-least-1-hour-after-start',
            'End time must be at least 1 hour after start time',
            function (endValue) {
              const { start_timestamp } = this.parent;

              if (typeof endValue !== 'number' || typeof start_timestamp !== 'number') return true;

              return endValue >= start_timestamp + DATA_RETRIEVAL_INTERVAL.ONE_HOUR;
            }
          ),
      time_grid_side: string()
        .oneOf(timeGridOptions.map(opt => opt.id), 'Invalid time grid interval')
        .required('This field is required'),
      is_now: boolean()
    }),
    onSubmit: async (values) => {
      const isNow = values.is_now;
      delete values.is_now;

      if (isNow) {
        values.end_timestamp = null;
      }

      let min_temperature = values.min_temperature;
      let max_temperature = values.max_temperature;

      if (measurement === CycleMeasurement.Fahrenheit) {
        min_temperature = Number(fahrenheitToCelsius(values.min_temperature));
        max_temperature = Number(fahrenheitToCelsius(values.max_temperature));
      }

      const payload = {
        ...values,
        min_temperature,
        max_temperature,
      };

      await save({ cycleId, payload });
      onClose(true);
    },
  });

  const disabledSubmit = Object.keys(chartPreferencesFormik.errors).length > 0 || Object.keys(chartPreferencesFormik.touched).length === 0;

  const changeDataTime = async (name: string, value: number) => {
    await chartPreferencesFormik.setFieldValue(name, value);
    await chartPreferencesFormik.setFieldTouched(name, true);
  };

  const changeReceivingNewData = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.target;

    if (!chartPreferencesFormik.touched[name]) {
      await chartPreferencesFormik.setFieldTouched(name);
    }

    if (checked) {
      const filteredErrors = Object.fromEntries(
        Object.entries(chartPreferencesFormik.errors).filter(([key]) => key !== 'end_timestamp')
      );
      chartPreferencesFormik.setErrors(filteredErrors);
    } else {
      await chartPreferencesFormik.validateField('end_timestamp');
    }
    await chartPreferencesFormik.setFieldValue(name, checked);

    return;
  }

  const fetchData = async () => {
    const values = await getOneByCycleId(cycleId);

    const data = Object.entries(values).reduce<ChartPreferencesFormValues>((acc, [key, value]) => {
      if (key === 'colors' || key === 'isDefaultTemplate') {
        return acc;
      }

      if (key === 'min_temperature' || key === 'max_temperature') {
        const tempValue = currentTempRange && values.isDefaultTemplate
          ? currentTempRange[key]
          : getTempByMeasurement(measurement, Number(value));

        return { ...acc, [key]: tempValue };
      }

      if (key === 'end_timestamp') {
        const isNow = value === null;
        return { ...acc, end_timestamp: isNow ? null : Number(value), is_now: isNow };
      }

      return { ...acc, [key]: Number(value) };
    }, {} as ChartPreferencesFormValues);

    chartPreferencesFormik.setValues(data);
    setIsFirstLoad(false);
  };

  const closeModal = () => {
    chartPreferencesFormik.resetForm();
    onClose(false);
  };

  const submitFormByBtn = (e: React.MouseEvent) => {
    e.preventDefault();
    chartPreferencesFormik.submitForm();
  };

  const submitForm = (e: React.FormEvent) => {
    e.preventDefault();
  };

  const getSelectedOption = () => {
    return timeGridOptions.find((option) => Number(option.id) === chartPreferencesFormik.values.time_grid_side)
  }

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <Modal
      modalTitle="Chart Preferences"
      isVisible={isVisible}
      onClose={closeModal}
      showAcceptBtn={false}
      showCancelBtn={false}
    >
      {isFirstLoad && <div className='min-h-[794px] 2xl:min-h-[890px] flex justify-center items-center'><SpinnerLoader /></div>}
      {!isFirstLoad && (
        <form className='c-chart-preference' onSubmit={submitForm} >
          <div className="pb-3" >
            <InputControl
              label='Temperature grid size'
              type='number'
              name='temperature_grid_size'
              placeholder="Enter number"
              value={chartPreferencesFormik.values.temperature_grid_size ?? 0}
              onChange={chartPreferencesFormik.handleChange}
              onBlur={chartPreferencesFormik.handleBlur}
              showError={chartPreferencesFormik.touched.temperature_grid_size && chartPreferencesFormik.errors.temperature_grid_size}
              errorText={chartPreferencesFormik.errors.temperature_grid_size}
            />
          </div>
          <div className="pb-3" >
            <InputControl
              label={<p className='flex w-full justify-between items-center flex-nowrap'><span>Min temperature</span><span>{CycleMeasurementSymbol[measurement]}</span></p>}
              type='number'
              placeholder="Enter number"
              name='min_temperature'
              value={chartPreferencesFormik.values.min_temperature}
              onChange={chartPreferencesFormik.handleChange}
              onBlur={chartPreferencesFormik.handleBlur}
              showError={chartPreferencesFormik.touched.min_temperature && chartPreferencesFormik.errors.min_temperature}
              errorText={chartPreferencesFormik.errors.min_temperature}
            />
          </div>
          <div className="pb-3" >
            <InputControl
              label={<p className='flex w-full justify-between items-center flex-nowrap'><span>Max temperature</span><span>{CycleMeasurementSymbol[measurement]}</span></p>}
              type='number'
              placeholder="Enter number"
              name='max_temperature'
              value={chartPreferencesFormik.values.max_temperature}
              onChange={chartPreferencesFormik.handleChange}
              onBlur={chartPreferencesFormik.handleBlur}
              showError={chartPreferencesFormik.touched.max_temperature && chartPreferencesFormik.errors.max_temperature}
              errorText={chartPreferencesFormik.errors.max_temperature}
            />
          </div>
          <div className="pb-3" >
            <div className='w-full'>
              <SelectOption
                name='time_grid_side'
                onChange={(e) => {
                  const selected = e.target.value as unknown as { id: string; value: string | number };
                  chartPreferencesFormik.setFieldValue('time_grid_side', Number(selected.id));
                  chartPreferencesFormik.setTouched({ ['time_grid_side']: true });
                }}
                options={timeGridOptions}
                selectedOption={getSelectedOption()}
                title='Interval grid size'
                placeholder='Select grid size interval'
                showError={(chartPreferencesFormik.touched.time_grid_side && chartPreferencesFormik.errors.time_grid_side) ? true : false}
                errorText={`${chartPreferencesFormik.errors.time_grid_side}`}
              />
            </div>
          </div>
          <div className="pb-3" >
            <InputDateControl
              label='Start time'
              name='start_timestamp'
              value={chartPreferencesFormik.values.start_timestamp ?? new Date().getTime()}
              handleChange={changeDataTime}
              showError={chartPreferencesFormik.touched.start_timestamp && chartPreferencesFormik.errors.start_timestamp}
              errorText={chartPreferencesFormik.errors.start_timestamp}
              onBlur={chartPreferencesFormik.handleBlur}
            />
          </div>
          <div className="pb-3" >
            {(!chartPreferencesFormik.values.is_now) && (
              <InputDateControl
                label='End time'
                name='end_timestamp'
                value={chartPreferencesFormik.values.end_timestamp ?? new Date().getTime()}
                handleChange={changeDataTime}
                showError={chartPreferencesFormik.touched.end_timestamp && chartPreferencesFormik.errors.end_timestamp}
                errorText={chartPreferencesFormik.errors.end_timestamp}
                onBlur={chartPreferencesFormik.handleBlur}
              />
            )}
          </div>
          <div className="pb-3" >
            <InputControl
              label='Now'
              type='checkbox'
              name='is_now'
              onBlur={chartPreferencesFormik.handleBlur}
              checked={Boolean(chartPreferencesFormik.values.is_now ?? false)}
              onChange={changeReceivingNewData}
              showError={chartPreferencesFormik.touched.is_now && chartPreferencesFormik.errors.is_now}
              errorText={chartPreferencesFormik.errors.is_now}
            />
          </div>
          <InteractiveButton
            disabled={disabledSubmit}
            text={t('buttons.save')}
            submit
            onClick={submitFormByBtn}
            isAdaptive
            className="w-full"
          />
        </form>
      )}
    </Modal>
  )
};

const mapDispatch = (dispatch: Dispatch) => ({
  getOneByCycleId: dispatch.chart_preference.getOneByCycleId,
  save: dispatch.chart_preference.update,
});

export default connect<null, DispatchProps, ChartPreferencesProps>(null, mapDispatch)(ChartPreferences);